import { Link } from "react-router-dom";
import { useBinderList, type BinderEntry } from "./binder-api.js";
import { entryTypeLabels } from "./binder-templates.js";

/**
 * Property File section on the property detail screen. Lists entries as
 * thumbnail-led cards (photos arrive in Slice 3 — a tinted initial stands in
 * for now), with a capture-first "Add" entry point.
 */
export function BinderSection({ propertyId }: { propertyId: string }) {
  const { data: entries, isLoading, error } = useBinderList(propertyId);

  return (
    <section className="border-t border-slate-200 px-4 py-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-900">Property file</h2>
        <Link
          to={`/app/properties/${propertyId}/binder/new`}
          className="inline-flex min-h-[36px] items-center gap-1 rounded-md bg-teal-700 px-3 text-sm font-medium text-white"
        >
          + Add
        </Link>
      </div>

      <div className="mt-3">
        {isLoading && <p className="text-sm text-slate-500">Loading…</p>}
        {error && (
          <p className="rounded bg-rose-50 p-3 text-sm text-rose-900">
            Couldn&apos;t load your property file.
          </p>
        )}
        {!isLoading && !error && (entries?.length ?? 0) === 0 && (
          <div className="rounded-lg border border-dashed border-slate-300 p-5 text-center">
            <p className="text-sm text-slate-600">
              Save photos, notes, and useful details for this property.
            </p>
          </div>
        )}
        {entries && entries.length > 0 && (
          <ul className="space-y-2">
            {entries.map((e) => (
              <li key={e.id}>
                <EntryCard propertyId={propertyId} entry={e} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function factSummary(entry: BinderEntry): string | null {
  const facts = Object.values(entry.facts ?? {});
  if (facts.length === 0) return null;
  return facts
    .slice(0, 2)
    .map((f) => `${typeof f.value === "boolean" ? (f.value ? "Yes" : "No") : f.value}${f.unit ? ` ${f.unit}` : ""}`)
    .join(" · ");
}

function EntryCard({ propertyId, entry }: { propertyId: string; entry: BinderEntry }) {
  const summary = factSummary(entry);
  return (
    <Link
      to={`/app/properties/${propertyId}/binder/${entry.id}`}
      className="flex gap-3 rounded-lg border border-slate-200 bg-white p-3 active:bg-slate-50"
    >
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-teal-50 text-base font-semibold text-teal-800">
        {entry.title.slice(0, 1).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-slate-900">{entry.title}</p>
        <p className="truncate text-sm text-slate-500">
          {[entry.locationLabel, entryTypeLabels[entry.entryType]].filter(Boolean).join(" · ")}
        </p>
        {summary && <p className="mt-0.5 truncate text-sm text-slate-500">{summary}</p>}
      </div>
    </Link>
  );
}
